import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronLeft, Loader2 } from "lucide-react";
import { Link, useRoute } from "wouter";
import { NotionMirror } from "@/components/portal/render/notion-mirror";
import { ProcessSteps } from "@/components/portal/render/process-steps";
import { RichDoc } from "@/components/portal/render/rich-doc";
import { SetupChecklist } from "@/components/portal/render/setup-checklist";
import { SlackChannelInfo } from "@/components/portal/render/slack-channel-info";
import { ToolSetup } from "@/components/portal/render/tool-setup";
import { Card } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { extractPortalErrorCode } from "@/lib/portal-rpc";
import { orpc } from "@/lib/rpc";

function CompanyContentInner({ resourceId }: { resourceId: string }) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const queryOpts = orpc.companyContent.getByResourceId.queryOptions({ input: { resourceId } });
  const content = useQuery(queryOpts);

  const refreshNotion = useMutation({
    ...orpc.companyContent.refreshContent.mutationOptions(),
    onSuccess: (fresh) => {
      queryClient.setQueryData(queryOpts.queryKey, fresh);
      toast({ title: "Conteúdo atualizado a partir do Notion." });
    },
    onError: () =>
      toast({ title: "Não foi possível sincronizar com o Notion.", variant: "destructive" }),
  });

  if (content.isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-24 gap-3">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        <p className="text-sm text-muted-foreground">
          Carregando conteúdo. Isso pode levar alguns instantes.
        </p>
      </div>
    );
  }

  if (content.error) {
    const code = extractPortalErrorCode(content.error);
    if (code === "resource_not_found") {
      return (
        <Card className="p-8 text-center space-y-1">
          <p className="text-sm font-medium">Conteúdo indisponível</p>
          <p className="text-xs text-muted-foreground">
            Pode ter sido removido ou ainda não foi publicado.
          </p>
        </Card>
      );
    }
    return (
      <Card className="p-6">
        <p className="text-sm text-muted-foreground">Não foi possível carregar este conteúdo.</p>
      </Card>
    );
  }

  const data = content.data!;

  const sanitizedContentBlocks = data.contentBlocks.map((block) => {
    if (
      block !== null &&
      typeof block === "object" &&
      (block as { type?: unknown }).type === "hero"
    ) {
      const { eyebrow: _eyebrow, ...rest } = block as Record<string, unknown>;
      return rest;
    }
    return block;
  });

  // Drop the page H1 when the first block is a hero whose headline/title
  // matches the resource title — otherwise the title shows up twice.
  const firstBlock = sanitizedContentBlocks[0] as Record<string, unknown> | undefined;
  const heroDuplicatesTitle =
    firstBlock?.type === "hero" &&
    [firstBlock.headline, firstBlock.title]
      .map((v) => (typeof v === "string" ? v.trim() : ""))
      .includes(data.title.trim());

  let body: React.ReactNode;
  switch (data.renderType) {
    case "notion_mirror":
      body = (
        <NotionMirror
          contentBlocks={sanitizedContentBlocks}
          notionPayload={data.notionPayload ?? []}
          staleWarning={data.staleWarning ?? false}
          empty={data.empty ?? false}
          syncedAt={data.syncedAt ?? null}
          sourceUrl={data.sourceUrl}
          onRefresh={() => refreshNotion.mutate({ resourceId })}
          isRefreshing={refreshNotion.isPending}
        />
      );
      break;
    case "process_steps":
      body = (
        <ProcessSteps
          contentBlocks={sanitizedContentBlocks}
          progressTrackable={false}
          checkedSteps={data.checkedSteps ?? []}
          readOnly={true}
          onToggleStep={() => {}}
        />
      );
      break;
    case "rich_doc":
      body = <RichDoc contentBlocks={sanitizedContentBlocks} />;
      break;
    case "setup_checklist":
      body = (
        <SetupChecklist
          tools={data.tools ?? []}
          readOnly={true}
          onToggle={() => {}}
          onAskOwner={() => {}}
          askingId={null}
        />
      );
      break;
    case "tool_setup":
      body = (
        <ToolSetup
          tool={data.tool ?? null}
          readOnly={true}
          onToggle={() => {}}
          onAskOwner={() => {}}
          askingId={null}
        />
      );
      break;
    case "slack_channel_info":
      body = <SlackChannelInfo channels={data.slackChannels ?? []} />;
      break;
    default:
      body = <RichDoc contentBlocks={sanitizedContentBlocks} />;
  }

  return (
    <div className="space-y-6">
      {!heroDuplicatesTitle && (
        <header>
          <h1 className="text-3xl font-semibold tracking-tight">{data.title}</h1>
        </header>
      )}
      {body}
    </div>
  );
}

export default function CompanySection() {
  const [, params] = useRoute<{ resourceId: string }>("/company/:resourceId");
  const resourceId = params?.resourceId ?? "";

  return (
    <div className="mx-auto w-full max-w-3xl p-6 space-y-4">
      <Link
        href="/company"
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
        data-testid="company-back-link"
      >
        <ChevronLeft className="h-4 w-4" />
        Voltar para o Handbook
      </Link>
      {resourceId ? (
        <CompanyContentInner resourceId={resourceId} />
      ) : (
        <Card className="p-6">
          <p className="text-sm text-muted-foreground">Recurso inválido.</p>
        </Card>
      )}
    </div>
  );
}
